import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/admin';
import { ApiError } from '../../api/client';
import { AdminLayout } from '../../components/AdminLayout';

export function AdminCustomerNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: adminApi.createCustomer,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
      navigate(`/admin/customers/${created.id}`);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create customer.');
    },
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    createMutation.mutate({ slug: slug.trim().toLowerCase(), displayName: displayName.trim() });
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <Link to="/admin" className="text-xs uppercase tracking-wider text-wc-muted-strong hover:text-wc-blue">
          ← Back to customers
        </Link>
        <h1 className="mt-2 font-serif text-2xl text-wc-black">New customer</h1>
        <p className="text-sm text-wc-muted-strong">
          Create the customer record first. You&apos;ll configure email domains,
          OIDC, and GitLab on the next screen.
        </p>
      </div>

      <form className="card max-w-2xl space-y-6 p-8" onSubmit={handleSubmit}>
        <div>
          <label className="field-label" htmlFor="customer-display-name">
            Display name
          </label>
          <input
            id="customer-display-name"
            className="field-input"
            type="text"
            placeholder="Acme Corp"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={120}
            required
          />
        </div>

        <div>
          <label className="field-label" htmlFor="customer-slug">
            Slug
          </label>
          <input
            id="customer-slug"
            className="field-input font-mono"
            type="text"
            placeholder="acme"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            pattern="^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$"
            maxLength={64}
            required
          />
          <p className="mt-1 text-xs text-wc-muted">
            URL-safe identifier used in the redirect URI registered with Entra ID
            (e.g. <code className="font-mono">/auth/callback/acme</code>). Lowercase
            letters, digits, and dashes only.
          </p>
        </div>

        {error && (
          <p className="text-sm text-wc-red" role="alert">
            {error}
          </p>
        )}

        <div className="flex items-center gap-4">
          <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create customer'}
          </button>
          <Link to="/admin" className="text-sm text-wc-muted-strong hover:text-wc-blue">
            Cancel
          </Link>
        </div>
      </form>
    </AdminLayout>
  );
}
